import React, { FC, useState } from "react";
import { LibraryTechnology, TechnologyCharge, TechnologyUnit } from "../../../data-types";
import { MatterRelationEditor } from "../matter-relation-editor";
import { Library } from "../library";
import { LibraryList } from "../library-list";
import { VpdList } from "../../vpd-list";
import { TechnologyChargeRow } from "../../technology-charge";
import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableItem, DraggableHandle } from "../../draggable-list";
import { useDebouncedCallback } from "use-debounce";

type MatterTechnologiesEditorProps = {
  technologyCharges: TechnologyCharge[];
  libraryTechnologies: LibraryTechnology[];
  technologyUnits: TechnologyUnit[];
  onUpdate: (technologyCharge: Partial<TechnologyCharge>) => void;
  onDelete: (id: number) => void;
  onCreate: (technologyCharge?: Partial<TechnologyCharge>) => void;
  onReorder: (activeId: number, overId: number) => void;
  onAutoFocus: () => void;
  currencySymbol: string;
};

export const MatterTechnologiesEditor: FC<MatterTechnologiesEditorProps> = ({
  technologyCharges,
  libraryTechnologies,
  technologyUnits,
  onUpdate,
  onDelete,
  onCreate,
  onReorder,
  onAutoFocus,
  currencySymbol,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const debouncedOnUpdate = useDebouncedCallback(
    (technologyCharge: Partial<TechnologyCharge>) => onUpdate(technologyCharge),
    600
  );
  const [activeTechnologyCharge, setActiveTechnologyCharge] = useState<TechnologyCharge | null>(
    null
  );

  return (
    <MatterRelationEditor
      libraryRender={(open, setOpen) => (
        <Library
          open={open}
          setOpen={setOpen}
          content={
            <LibraryList
              itemKeyProperty="id"
              itemTextProperty="description"
              loading={false}
              items={libraryTechnologies}
              selectedItems={libraryTechnologies.filter((lib) =>
                technologyCharges.some((charge) => charge.libraryTechnologyId === lib.id)
              )}
              copyLibraryItem={(item) => {
                const { id: libraryTechnologyId, description, technologyUnitId } = item;
                onCreate({ libraryTechnologyId, description, technologyUnitId });
              }}
              labels={{
                emptyText: "No Library Technologies available.",
                loadingText: "Loading Library Technologies...",
              }}
            />
          }
        />
      )}
      workspaceContent={
        <DndContext
          onDragStart={({ active }) => {
            setActiveTechnologyCharge(
              technologyCharges.find((tc) => tc.id.toString() === active.id)!
            );
          }}
          onDragEnd={({ active, over }) =>
            over && onReorder(parseInt(active.id), parseInt(over.id))
          }
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={technologyCharges.map((tc) => tc.id.toString())}
            strategy={verticalListSortingStrategy}
          >
            <VpdList
              itemKeyProperty="id"
              itemTextProperty="description"
              loading={false}
              split={false}
              items={technologyCharges}
              renderItem={(item: TechnologyCharge) => {
                const isAutoFocused = item?.autoFocus ?? false;
                return (
                  <SortableItem id={item.id.toString()} key={item.id}>
                    {({ attributes, listeners }) => (
                      <VpdList.Item
                        key={item.id.toString()}
                        content={
                          <TechnologyChargeRow
                            technologyCharge={item}
                            autoFocus={isAutoFocused}
                            onAutoFocus={onAutoFocus}
                            libraryTechnologies={libraryTechnologies}
                            technologyUnits={technologyUnits}
                            onUpdate={debouncedOnUpdate}
                            onDelete={onDelete}
                            currencySymbol={currencySymbol}
                          />
                        }
                        contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
                      />
                    )}
                  </SortableItem>
                );
              }}
              listStyle="primary"
              labels={{
                emptyText: "No items available.",
                loadingText: "Loading items...",
              }}
            />
          </SortableContext>
          <DragOverlay>
            {activeTechnologyCharge ? (
              <VpdList
                itemKeyProperty="id"
                itemTextProperty="text"
                loading={false}
                split={false}
                items={[activeTechnologyCharge]}
                renderItem={(item: TechnologyCharge) => (
                  <VpdList.Item
                    key="rubbish"
                    content={
                      <TechnologyChargeRow
                        technologyCharge={item}
                        libraryTechnologies={libraryTechnologies}
                        technologyUnits={technologyUnits}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                        currencySymbol={currencySymbol}
                      />
                    }
                    contentPrefix={<DraggableHandle />}
                  />
                )}
                listStyle="primary"
                labels={{
                  emptyText: "No items available.",
                  loadingText: "Loading items...",
                }}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      }
      showAddNew={false}
      addNew={undefined}
      showHeader={false}
    />
  );
};
